import { useState, useEffect } from "react";
import { observer } from "mobx-react-lite";
import {
  Box,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useColorScheme,
  CircularProgress,
  Skeleton,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import TopicOutlinedIcon from '@mui/icons-material/TopicOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import SyncAltOutlinedIcon from '@mui/icons-material/SyncAltOutlined';
import ElectricBoltOutlinedIcon from '@mui/icons-material/ElectricBoltOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import PauseOutlinedIcon from '@mui/icons-material/PauseOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import { useSnackbar } from 'notistack';
import { useNavigate } from "react-router-dom";
import {
  deleteProject,
  loadProject,
  createProject,
  createProspectProject,
  updateProject,
  listUsers,
  listUsersForSharedProject,
  shareProject,
  unshareProject,
} from "../../service/api_service";
import { getProjectDomain, numberWithK } from "../../utils/stringFormat";
import { Constants } from "../../utils/constants";
import {
  ActiveButton,
  PendingButton,
  PausedButton,
  SearchInput,
  MainButton,
  ProjectInfoWidget,
  ProjectCreateModal,
  ProjectEditModal,
  ProjectDeleteModal,
  SkeletonTableWidget,
  ProjectShareModal,
} from "../index";
import { isActionProject } from "../../utils/helper";

function ProjectList({ jobStore, authStore }) {
  const { mode } = useColorScheme();
  const { enqueueSnackbar } = useSnackbar();
  const user = authStore.user;
  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    // "&:nth-of-type(odd)": {
    //   backgroundColor: theme.palette.action.hover
    // },
    borderBottom: `1px solid ${Constants.Colors[mode].border} !important`,
    // hide last border
    "&:last-child td, &:last-child th": {
      border: 0
    }
  }));

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    borderBottom: `1px solid ${Constants.Colors[mode].border}`,
    color: Constants.Colors[mode].mutedForeground,
  }));

  jobStore.setNavigation("automated");

  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [projectList, setProjectList] = useState([]);
  const [filteredProjectList, setFilteredProjectList] = useState([]);

  const navigate = useNavigate();

  const [deleting, setDeleting] = useState([]);
  const [playPause, setPlayPause] = useState([]);

  const [visible, setVisible] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editData, setEditData] = useState({});

  const [shareProjectLoading, setShareProjectLoading] = useState(false);
  const [shareProjectDialog, setShareProjectDialog] = useState(false);

  const [selectedProjectValues, setSelectProjectValues] = useState([]);
  const [sharedProjectOptions, setSharedProjectOption] = useState([]);
  const [prospectProjectKey, setProspectProjectKey] = useState("");

  const [lastEvaluatedKey, setLastEvaluatedKey] = useState("");
  const [loadMoreLoading, setLoadMoreLoading] = useState(false);

  const handleShareClick = async (project) => {
    //setSelectProjectValues
    setShareProjectLoading(true);
    setShareProjectDialog(true);
    setEditData(project);
    const users = await listUsersForSharedProject(project.project_id);
    setSelectProjectValues(users.map((x) => x.user_id));
    setShareProjectLoading(false);
  };

  const handleChange = (value) => {
    shareProject(editData.project_id, value);
    setSelectProjectValues(value);
  };

  const onFinish = async ({ projectName, projectSiteMap }) => {
    const createdProject = await createProject(
      projectName,
      projectSiteMap.split("\n"),
      prospectProjectKey
    );
    await getProjects();
    setVisible(false);
    enqueueSnackbar("Project created successfully", { variant: "success", autoHideDuration: 4000, anchorOrigin: { vertical: "top", horizontal: "right" } });
  };

  const onEditFinish = async (project) => {
    await updateProject(project.project_id, {
      sitemap_xml: project.sitemap_xml,
      utm_source: project.utm_source,
      utm_medium: project.utm_medium,
      utm_enabled: project.utm_enabled,
      email_report: project.email_report,
      block_urls: project.block_urls,
      font_size: project.font_size,
      font_color: project.font_color,
      background_color: project.background_color,
      redirect_message: project.redirect_message,
    });
    await getProjects();
    setShowEditForm(false);
  };

  const showEditFormModal = (record) => {
    console.log(record);
    setShowEditForm(true);
    setEditData(record);
  };

  const onCreateProject = async () => {
    setVisible(true);
    const prospect = await createProspectProject();
    setProspectProjectKey(prospect.prospect_project_id);
  };

  const showDeleteConfirm = (project) => {
    setEditData(project);
    setShowDeleteModal(true);
  };

  const performDeletion = async (projectId) => {
    setDeleting([...deleting, projectId]);
    setShowDeleteModal(false);
    try {
      await deleteProject(projectId);
      await getProjects();
    } finally {
      setDeleting(deleting.splice(deleting.indexOf(projectId), 1));
    }
  };

  const togglePlayPause = async (projectId, value) => {
    setPlayPause([...playPause, projectId]);
    try {
      await updateProject(projectId, { project_pause: value });
      await getProjects();
    } finally {
      setPlayPause(playPause.splice(playPause.indexOf(projectId), 1));
    }
  };

  const getProjects = async (isLoadMore = false) => {
    const projects = await loadProject(isLoadMore ? lastEvaluatedKey : null, 15);
    if (Array.isArray(projects)) {
      setProjectList(projects);
      setLastEvaluatedKey("");
      setFilteredProjectList(projects);
    } else {
      if (isLoadMore) {
        setProjectList([...projectList, ...projects["items"]]);
        setFilteredProjectList([...filteredProjectList, ...projects["items"]]);
      } else {
        setProjectList(projects["items"]);
        setFilteredProjectList(projects["items"]);
      }
      setLastEvaluatedKey(projects["lastEvaluatedKey"]);
    }
    setSearchText("");
  }

  const loadMore = async () => {
    setLoadMoreLoading(true);
    await getProjects(true);
    setLoadMoreLoading(false);
  }

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await getProjects();
      const users = await listUsers();
      setSharedProjectOption(
        users.map((x) => {
          return {
            label: x.user_id,
            value: x.user_id,
          };
        })
      );
      setLoading(false);
    };
    fetchData();
  }, []);

  const script = () => {
    const src = `${window.location.origin}/dist/app.bundle.js`;
    // eslint-disable-next-line no-useless-escape
    const script = `<script src="${src}" class="${prospectProjectKey}" id="redirectMapperID"><\/script>`;
    return script;
  };

  const getTotalRedirects = () => {
    const counts = projectList.reduce((acc, cur) => acc + cur.redirect_count_month, 0);
    return counts;
  }

  return (
    <Box>
      <ProjectShareModal
        open={shareProjectDialog}
        onClose={() => {
          setShareProjectDialog(false);
          setEditData(null);
        }}
        project={editData}
        value={selectedProjectValues}
        options={sharedProjectOptions}
        loading={shareProjectLoading}
        onChange={handleChange}
      />
      <ProjectCreateModal
        open={visible}
        onClose={() => {
          setVisible(false);
        }}
        script={script()}
        onFinish={async ({ name, sitemap }) => {
          onFinish({ projectName: name, projectSiteMap: sitemap });
        }}
      />
      <ProjectEditModal
        open={showEditForm}
        onClose={() => {
          setShowEditForm(false);
        }}
        project={editData}
        onFinish={(project) => {
          onEditFinish(project);
        }}
      />
      <ProjectDeleteModal
        open={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
        }}
        project={editData}
        onFinish={(project) => {
          performDeletion(project.project_id);
        }}
      />
      <Stack
        direction={{ xs: "column", sm: "column", md: "row" }}
        justifyContent={"space-between"}
        alignItems={"center"}
        spacing={2}
      >
        <Box>
          <Typography fontWeight={Constants.Styles.fontWeight.semiBold} fontSize={Constants.Styles.fontSize["2xl"]}>
            Projects
          </Typography>
          <Typography fontSize={Constants.Styles.fontSize.sm} fontWeight={400} color={Constants.Colors.light.mutedForeground}>
            Manage your redirect projects and monitor 404 recovery
          </Typography>
        </Box>
        {isActionProject(user) && <Box
          sx={{
            width: { xs: "100%", sm: "100%", md: "auto" },
          }}
        >
          <MainButton
            text="Create Project"
            size="medium"
            preIcon={<AddOutlinedIcon />}
            onClick={onCreateProject}
          />
        </Box>}
      </Stack>
      <Grid container spacing={2} mt={3}>
        <Grid item size={{ xs: 12, sm: 6, md: 3 }} key={"total_projects"}>
          <ProjectInfoWidget
            isLoading={loading}
            title="Total Projects"
            value={projectList.length}
            des="Across all domains"
            icon={<TopicOutlinedIcon sx={{ fontSize: "1.5rem" }} />}
          />
        </Grid>
        <Grid item size={{ xs: 12, sm: 6, md: 3 }} key={"total_redirects"}>
          <ProjectInfoWidget
            isLoading={loading}
            title="Total Redirects"
            value={numberWithK(getTotalRedirects())}
            des="last 30 days"
            icon={<SyncAltOutlinedIcon sx={{ fontSize: "1.5rem" }} />}
          />
        </Grid>
        <Grid item size={{ xs: 12, sm: 6, md: 3 }} key={"active_projects"}>
          <ProjectInfoWidget
            isLoading={loading}
            title="Active Projects"
            value={projectList.filter((x) => !x?.hasOwnProperty('pause') || x?.pause === false).length}
            des="Currently redirecting"
            icon={<ElectricBoltOutlinedIcon sx={{ fontSize: "1.5rem" }} />}
          />
        </Grid>
        <Grid item size={{ xs: 12, sm: 6, md: 3 }} key={"avg_daily_redirects"}>
          <ProjectInfoWidget
            isLoading={loading}
            title="Avg. Daily Redirects"
            value={projectList.length > 0 ? numberWithK(getTotalRedirects() / projectList.length) : 0}
            des="+12% from last month"
            icon={<TrendingUpOutlinedIcon sx={{ fontSize: "1.5rem" }} />}
          />
        </Grid>
      </Grid>
      {!loading && projectList.length > 0 && (
        <Box>
          <Box
            sx={{
              maxWidth: 300,
              padding: "24px 0",
            }}
          >
            <SearchInput
              placeholder="Search projects..."
              sx={{
                width: "100%",
              }}
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setFilteredProjectList(
                  projectList.filter((project) =>
                    project.name.toLowerCase().includes(e.target.value.toLowerCase())
                  )
                );
              }}
            />
          </Box>
          <TableContainer
            sx={{
              borderRadius: "6px",
              border: `1px solid ${Constants.Colors[mode].border}`,
            }}
          >
            <Table sx={{
              minWidth: 650,
            }} aria-label="project table">
              <TableHead>
                <StyledTableRow>
                  <StyledTableCell sx={{
                    fontWeight: Constants.Styles.fontWeight.semiBold,
                  }}>Project</StyledTableCell>
                  <StyledTableCell sx={{
                    fontWeight: Constants.Styles.fontWeight.semiBold,
                  }}>Domain</StyledTableCell>
                  <StyledTableCell sx={{
                    fontWeight: Constants.Styles.fontWeight.semiBold,
                  }}>30 Day Redirects</StyledTableCell>
                  <StyledTableCell sx={{
                    fontWeight: Constants.Styles.fontWeight.semiBold,
                  }}>Status</StyledTableCell>
                  <StyledTableCell sx={{
                    fontWeight: Constants.Styles.fontWeight.semiBold,
                  }} align="right">Actions</StyledTableCell>
                </StyledTableRow>
              </TableHead>
              <TableBody>
                {filteredProjectList.map((project, index) => {
                  const playPauseValue = project?.pause ? project.pause : false;
                  const domain = getProjectDomain(project?.sitemap_xml);
                  const tooltipStyle = {
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "4px",
                    borderRadius: "4px",
                    "&:hover": {
                      backgroundColor: Constants.Colors[mode].accent,
                      color: Constants.Colors[mode].foreground,
                    }
                  };
                  const tooltipProperty = {
                    tooltip: {
                      sx: {
                        color: Constants.Colors[mode].background,
                        backgroundColor: Constants.Colors[mode].foreground,
                        fontSize: Constants.Styles.fontSize.sm,
                      },
                    },
                  };
                  const isActionable = isActionProject(user, project);
                  return (
                    <StyledTableRow key={`${project.project_id}_${index}`}>
                      <StyledTableCell sx={{
                        fontWeight: 700,
                        cursor: "pointer",
                        color: Constants.Colors[mode].foreground,
                        "&:hover": {
                          color: Constants.Colors[mode].accent,
                        }
                      }} onClick={() => {
                        navigate(`/project/${project.project_id}`)
                      }}>{project.name}</StyledTableCell>
                      <StyledTableCell
                        sx={{
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          if (domain) {
                            window.open(`https://${domain}`, "_blank");
                          }
                        }}
                      >
                        {domain && <Stack direction={"row"} alignItems={"center"} justifyContent={"start"} spacing={1}>
                          <span>
                            {domain}</span> <OpenInNewOutlinedIcon sx={{
                              fontSize: "14px",
                            }} />
                        </Stack>}
                      </StyledTableCell>
                      <StyledTableCell>{numberWithK(project?.redirect_count_month ?? 0)}</StyledTableCell>
                      <StyledTableCell>{
                        playPauseValue ? <PausedButton /> : project.status === 'complete' ? <ActiveButton /> : <PendingButton />}</StyledTableCell>
                      <StyledTableCell align="right">
                        {isActionable && <Stack direction={"row"} spacing={1} alignItems={"center"} justifyContent={"end"}>
                          <Tooltip
                            title="Share"
                            slotProps={{
                              ...tooltipProperty,
                            }}
                          >
                            <Box
                              sx={{
                                ...tooltipStyle,
                              }}
                            >
                              <ShareOutlinedIcon sx={{
                                fontSize: "1.2rem",
                                cursor: "pointer",
                              }} onClick={() => {
                                handleShareClick(project);
                              }}
                              />
                            </Box>
                          </Tooltip>
                          {playPause.indexOf(project.project_id) >= 0 ? (
                            <CircularProgress size={20} />
                          ) : playPauseValue ? (
                            <Tooltip title="Pause" slotProps={{
                              ...tooltipProperty,
                            }}>
                              <Box sx={{
                                ...tooltipStyle,
                              }}>
                                <PlayArrowOutlinedIcon sx={{
                                  fontSize: "1.2rem",
                                  cursor: "pointer",
                                }} onClick={() =>
                                  togglePlayPause(project.project_id, !playPauseValue)
                                } />
                              </Box>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Activate" slotProps={{
                              ...tooltipProperty,
                            }}>
                              <Box sx={{
                                ...tooltipStyle,
                              }}>
                                <PauseOutlinedIcon sx={{
                                  fontSize: "1.2rem",
                                  cursor: "pointer",
                                }} onClick={() =>
                                  togglePlayPause(project.project_id, !playPauseValue)
                                } />
                              </Box>
                            </Tooltip>
                          )}
                          <Tooltip title="Edit" slotProps={{
                            ...tooltipProperty,
                          }}>
                            <Box sx={{
                              ...tooltipStyle,
                            }}>
                              <EditOutlinedIcon sx={{
                                fontSize: "1.2rem",
                                cursor: "pointer",
                              }} onClick={() => showEditFormModal(project)} />
                            </Box>
                          </Tooltip>
                          <Tooltip title="Delete" slotProps={{
                            ...tooltipProperty,
                          }}>
                            <Box sx={{
                              ...tooltipStyle,
                            }}>
                              <DeleteForeverOutlinedIcon sx={{
                                fontSize: "1.2rem",
                                cursor: "pointer",
                              }} onClick={() =>
                                showDeleteConfirm(project)
                              } />
                            </Box>
                          </Tooltip>
                        </Stack>}
                      </StyledTableCell>
                    </StyledTableRow>);
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Stack sx={{ marginTop: "20px" }} direction={"row"} justifyContent={"center"} alignItems={"center"}>
            {!loadMoreLoading && lastEvaluatedKey && <MainButton
              size="small"
              text="Load More"
              onClick={loadMore}
              fullWidth={false}
            />}
            {loadMoreLoading && <CircularProgress color="success" size={32} />}
          </Stack>
        </Box>
      )}
      {!loading && projectList.length === 0 && (
        <Typography sx={{ fontSize: Constants.Styles.fontSize.base, mt: 2, }}>No project has been created yet.</Typography>
      )}
      {loading && <Box>
        <Box
          sx={{
            maxWidth: 300,
            padding: "24px 0",
          }}
        >
          <Skeleton variant="rounded" sx={{ height: 40 }} />
        </Box>
        <SkeletonTableWidget columns={5} rowCount={5} />
      </Box>}
    </Box>
  );
}

export default observer(ProjectList);
